import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { datadogLogs } from '@datadog/browser-logs';
import config from 'config';

interface DatadogUser {
  id: string;
  email?: string;
  role?: string;
}

type DatadogContextValue = string | number | boolean | undefined;

type DatadogContext = Record<string, DatadogContextValue>;

export const DATADOG_EVENT = {
  apiCall: 'api_call',
  routeChange: 'route_change',
  adsLibraryNetworkChange: 'ads_library_network_change',
  adsLibraryRegionChange: 'ads_library_region_change',
  adsLibraryFilterApply: 'ads_library_filter_apply',
  adsLibraryCreativeView: 'ads_library_creative_view',
} as const;

const DEFAULT_ENV = window.location.hostname.includes('-stage.asqq.io')
  ? 'stage'
  : window.location.hostname.includes('-dev.asqq.io')
    ? 'dev'
    : 'production';

const DATADOG_SERVICE = 'wellspy-admin';

let isDatadogInitialized = false;

const trackDatadogAction = (name: string, context?: DatadogContext) => {
  if (!isDatadogInitialized) {
    return;
  }

  datadogRum.addAction(name, context);
};

const initDatadogRum = () => {
  if (isDatadogInitialized || import.meta.env.MODE === 'test') {
    return;
  }

  if (!config.DATADOG_APPLICATION_ID || !config.DATADOG_CLIENT_ID) {
    console.warn(
      'Datadog RUM skipped: set DATADOG_APPLICATION_ID and DATADOG_CLIENT_ID in config.js',
    );
    return;
  }

  datadogRum.init({
    applicationId: config.DATADOG_APPLICATION_ID,
    clientToken: config.DATADOG_CLIENT_ID,
    site: 'datadoghq.com',
    service: DATADOG_SERVICE,
    env: DEFAULT_ENV,
    version: '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'allow',
    plugins: [reactPlugin({ router: false })],
  });

  datadogLogs.init({
    clientToken: config.DATADOG_CLIENT_ID,
    site: 'datadoghq.com',
    service: DATADOG_SERVICE,
    env: DEFAULT_ENV,
    version: '1.0.0',
    sessionSampleRate: 100,
    forwardErrorsToLogs: false,
    forwardConsoleLogs: [],
    forwardReports: 'all',
  });

  console.info('Datadog Rum initialized!', {
    clientToken: !!config.DATADOG_CLIENT_ID,
    applicationId: !!config.DATADOG_APPLICATION_ID,
  });

  isDatadogInitialized = true;
};

const setDatadogUser = ({ id, email, role }: DatadogUser) => {
  if (!isDatadogInitialized) {
    return;
  }

  datadogRum.setUser({
    id,
    email,
    role,
  });
  datadogLogs.setUser({
    id,
    email,
    role,
  });
};

const trackDatadogError = (error: Error | string, context?: DatadogContext) => {
  if (!isDatadogInitialized) {
    return;
  }

  datadogRum.addError(error, context);

  datadogLogs.logger.error(
    typeof error === 'string' ? error : error.message,
    {
      ...context,
      errorName: typeof error === 'string' ? 'Error' : error.name,
      errorMessage: typeof error === 'string' ? error : error.message,
      errorStack: typeof error === 'string' ? undefined : error.stack,
      trackedBy: 'trackDatadogError',
    },
    error instanceof Error ? error : undefined,
  );
};

const datadogActions = {
  initDatadogRum,
  setDatadogUser,
  trackDatadogAction,
  trackDatadogError,
};

export default datadogActions;
export * from './adsLibraryTracking';
